import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Table, DatePicker, Row, Col, Space, Typography, message, Modal } from 'antd';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import { useParams } from 'react-router-dom';

const { Option } = Select;
const { Title } = Typography;

const EditSales = () => {
  const { id } = useParams(); // Get the sale ID from the URL
  const [form] = Form.useForm();
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [products, setProducts] = useState([{ id: '', quantity: '', selling_price: '' }]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axiosInstance.get('/customers');
        setCustomers(response.data.customers);
      } catch (error) {
        message.error('Failed to fetch customers.');
      }
    };

    const fetchAccounts = async () => {
      try {
        const response = await axiosInstance.get('/account');
        setAccounts(response.data);
        setFilteredAccounts(response.data);
      } catch (error) {
        message.error('Failed to fetch accounts.');
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await axiosInstance.get('/products');
        setProductsList(response.data.products);
      } catch (error) {
        message.error('Failed to fetch products.');
      }
    };

    const fetchSaleDetails = async () => {
      try {
        const response = await axiosInstance.get(`/sales/view/${id}`);
        const saleData = response.data;

        // Populate form with sale details
        form.setFieldsValue({
          type: saleData.type,
          customer: saleData.customer.id,
          account: saleData.account.id,
          invoice_number: saleData.invoice_number,
          reverse_receipt_id: saleData.reverse_receipt_id,
          due_date: saleData.due_date ? moment(saleData.due_date) : null,
          payment_date: saleData.payment_date ? moment(saleData.payment_date) : null,
        });

        // Populate products in the table
        setProducts(
          saleData.items.map((item) => ({
            id: item.product.id,
            quantity: item.quantity,
            selling_price: item.product.selling_price,
          }))
        );

        setSelectedType(saleData.type);
      } catch (error) {
        message.error('Failed to fetch sale details.');
      }
    };

    fetchCustomers();
    fetchAccounts();
    fetchProducts();
    fetchSaleDetails();
  }, [id]); // Only fetch data when the ID changes

  const handleProductChange = (index, field, value) => {
    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts];

      if (field === 'id') {
        updatedProducts[index][field] = value;

        const selectedProduct = productsList.find((prod) => prod.id === value);
        if (selectedProduct) {
          updatedProducts[index]['selling_price'] = selectedProduct.selling_price || 0;
        }
      } else {
        updatedProducts[index][field] = value;
      }

      return updatedProducts;
    });
  };

  const addProductRow = () => {
    setProducts([...products, { id: '', quantity: '', selling_price: '' }]);
  };

  const removeProductRow = (index) => {
    const updatedProducts = [...products];
    updatedProducts.splice(index, 1);
    setProducts(updatedProducts);
  };

  const filterAccountsByType = (type) => {
    setFilteredAccounts(accounts.filter(account => account.type === type));
  };

  const confirmSubmit = (values) => {
    Modal.confirm({
      title: 'Confirm Sales Submission',
      content: 'Are you sure you want to submit this sales order?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => handleSubmit(values),
    });
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    const payload = {
      customer_id: values.customer,
      account_id: values.account,
      products: products.map((product) => ({
        id: product.id,
        quantity: product.quantity,
        selling_price: product.selling_price,
      })),
      invoice_number: values.invoice_number,
      type: values.type,
      due_date: values.due_date || null,
      payment_date: values.payment_date || null,
      reverse_receipt_id: values.reverse_receipt_id,
      total: totalPrice,
    };

    try {
      const validationResponse = await axiosInstance.post('/sales/validate-quantities', { products: payload.products });

      if (validationResponse.data.error) {
        message.error(validationResponse.data.error);
        return;
      }

      const createResponse = await axiosInstance.post('/sales/create', payload);

      if (createResponse.status === 200) {
        message.success('Sales Created Successfully!');
        form.resetFields();
        setProducts([{ id: '', quantity: '', selling_price: '' }]);
      } else {
        message.error('Failed to create Sales Order. Please try again.');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        message.error(error.response.data.error);
      } else {
        message.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const productColumns = [
    {
      title: 'Product',
      dataIndex: 'id',
      render: (_, record, index) => (
        <Select
          value={record.id}
          onChange={(value) => handleProductChange(index, 'id', value)}
          style={{ width: '100%' }}
          placeholder="Select Product"
        >
          {productsList.map((prod) => (
            <Option key={prod.id} value={prod.id}>
              {prod.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      render: (_, record, index) => (
        <Input
          type="number"
          value={record.quantity}
          onChange={(e) => handleProductChange(index, 'quantity', parseInt(e.target.value, 10) || '')}
          min={1}
          placeholder="Enter Quantity"
        />
      ),
    },
    {
      title: 'Selling Price',
      dataIndex: 'selling_price',
      render: (_, record, index) => (
        <Input
          type="number"
          value={record.selling_price}
          onChange={(e) => handleProductChange(index, 'selling_price', parseFloat(e.target.value) || 0)}
          min={0}
          placeholder="Enter Price"
          disabled
          style={{ fontWeight: 'bold', color: 'black' }}
        />
      ),
    },
    {
      title: 'Total',
      render: (_, record) => (record.quantity * record.selling_price).toFixed(2),
    },
    {
      title: 'Action',
      render: (_, __, index) => (
        index > 0 && (
          <Button type="link" danger onClick={() => removeProductRow(index)}>
            Remove
          </Button>
        )
      ),
    },
  ];

  const calculateTotal = () => {
    let totalQuantity = 0;
    let totalPrice = 0;

    products.forEach((product) => {
      totalQuantity += product.quantity;
      totalPrice += product.quantity * product.selling_price;
    });

    return { totalQuantity, totalPrice };
  };

  const { totalQuantity, totalPrice } = calculateTotal();

  const handleValuesChange = (changedValues) => {
    if (changedValues.type) {
      setSelectedType(changedValues.type);
      const resetFields = Object.keys(changedValues).filter(field => field !== 'type');
      form.resetFields(resetFields);
    }
  };

  return (
    <div>
      <Title level={3}>Edit Sales</Title>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        onFinish={confirmSubmit}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Type"
              name="type"
              rules={[{ required: true, message: 'Please select the type' }]}>
              <Select
                onChange={(value) => {
                  filterAccountsByType(value);
                  form.setFieldsValue({ account: undefined });
                }}
                placeholder="Select Type"
              >
                <Option value="sale">Sale</Option>
                <Option value="return">Return</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Customer"
              name="customer"
              rules={[{ required: true, message: 'Please select a customer' }]}>
              <Select placeholder="Select Customer">
                {customers.map((customer) => (
                  <Option key={customer.id} value={customer.id}>
                    {customer.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Account"
              name="account"
              rules={[{ required: true, message: 'Please select an account' }]}>
              <Select placeholder="Select Account">
                {filteredAccounts.map((account) => (
                  <Option key={account.id} value={account.id}>
                    {account.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Invoice Number" name="invoice_number">
              <Input placeholder="Enter Invoice Number" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Reverse Receipt ID" name="reverse_receipt_id">
              <Input placeholder="Enter Reverse Receipt ID" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Due Date" name="due_date">
              <DatePicker placeholder="Select Due Date" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Payment Date" name="payment_date">
              <DatePicker placeholder="Select Payment Date" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Table
          columns={productColumns}
          dataSource={products}
          rowKey="id"
          pagination={false}
          footer={() => (
            <div>
              <Space>
                <strong>Total Quantity:</strong> {totalQuantity}
              </Space>
              <Space>
                <strong>Total Amount:</strong> {totalPrice.toFixed(2)}
              </Space>
            </div>
          )}
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Button
              type="dashed"
              onClick={addProductRow}>
              Add Product
            </Button>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}>
              Submit Sales
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default EditSales;
